"use client";

import { c as _c } from "react/compiler-runtime";
import { FaAngleUp } from "react-icons/fa";
import SocialIcons from "../header/SocialIcons";
import Link from "@/components/ui/Link";
import Image from "next/image";
import { useEffect, useState } from "react";
import { getHeaderFooterConfig, getMenuGroups, MenuGroupItem } from "@/lib/api";
import { getLatestNewsHeadlines, LatestNewsHeadline } from "@/lib/latestNews";
import { resolveAssetUrl } from "@/lib/utils";
import type { HeaderFooterConfig, FooterGroupEntry, FooterLinkEntry } from "@/lib/local-store/types";
import { DEFAULT_HEADER_FOOTER_CONFIG } from "@/lib/local-store/defaultHeaderFooter";

// Fallback badges, used only when a config entry has no imageUrl of its own.
const APP_BADGE_IMAGES: Record<string, {
  src: string;
}> = {
  "App Store": {
    src: "https://media.dinamani.com/dinamani/2026-02-27/73dqc9df/appstore.png"
  },
  "Google Play": {
    src: "https://media.dinamani.com/dinamani/2026-02-27/gdkfu5wb/googleplay.png"
  },
  "Play Store": {
    src: "https://media.dinamani.com/dinamani/2026-02-27/gdkfu5wb/googleplay.png"
  }
};

// Relative URLs and the main dinamani.com host resolve to their path (internal
// nav); any other host (subdomains included) is treated as external.
function resolveFooterHref(raw: string | undefined | null): {
  href: string;
  external: boolean;
} | null {
  if (!raw) return null;
  try {
    const base = typeof window !== "undefined" ? window.location.origin : "https://www.dinamani.com";
    const parsed = new URL(raw, base);
    const isDinamani = parsed.hostname === "www.dinamani.com" || parsed.hostname === "dinamani.com";
    if (isDinamani) {
      return {
        href: parsed.pathname + parsed.search,
        external: false
      };
    }
    return {
      href: raw,
      external: true
    };
  } catch {
    if (raw.startsWith("http://") || raw.startsWith("https://")) {
      return {
        href: raw,
        external: true
      };
    }
    return {
      href: raw.startsWith("/") ? raw : `/${raw}`,
      external: false
    };
  }
}
const FooterLink = t0 => {
  const $ = _c(6);
  const {
    href: t1,
    external: t2,
    children
  } = t0;
  const href = t1 === undefined ? "#" : t1;
  const external = t2 === undefined ? false : t2;
  if (external) {
    let t3;
    if ($[0] !== children || $[1] !== href) {
      t3 = <Link href={href} target="_blank" rel="noreferrer" className="text-FFFFFF hover:opacity-80 transition-opacity text-12-tamil-600">{children}</Link>;
      $[0] = children;
      $[1] = href;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    return t3;
  }
  let t3;
  if ($[3] !== children || $[4] !== href) {
    t3 = <Link href={href} className="text-FFFFFF hover:opacity-80 transition-opacity text-12-tamil-600">{children}</Link>;
    $[3] = children;
    $[4] = href;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};

// Static class lookup — Tailwind's JIT purge only picks up class names it can
// see literally in source, so a template-built `grid-cols-${n}` would be stripped.
const GRID_COLS_CLASSES: Record<number, string> = {
  1: "sm:grid-cols-1 lg:grid-cols-1",
  2: "sm:grid-cols-2 lg:grid-cols-2",
  3: "sm:grid-cols-3 lg:grid-cols-3",
  4: "sm:grid-cols-4 lg:grid-cols-4"
};
function resolveGroupLinks(group: FooterGroupEntry) {
  return group.links.map((link: FooterLinkEntry) => {
    const resolved = resolveFooterHref(link.url);
    return resolved ? {
      ...link,
      ...resolved
    } : null;
  }).filter((l): l is FooterLinkEntry & {
    href: string;
    external: boolean;
  } => l !== null);
}

// Center-column groups: "row" layout renders as a plain link row (no visible
// title, matching the original static top-nav), "columns" renders as a
// titled grid block (matching the original sections grid).
function FooterGroupSection(t0) {
  const $ = _c(15);
  const {
    group
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== group) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const resolvedLinks = resolveGroupLinks(group);
      if (group.layout === "row") {
        t5 = <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-FFFFFF text-12-english-500 border-b border-2D497E p-5">{resolvedLinks.map(_temp)}</div>;
        break bb0;
      }
      t3 = "border-b border-2D497E";
      if ($[6] !== group.title) {
        t4 = <div className="flex p-5 items-center gap-2 text-FFFFFF text-12-english-500 uppercase tracking-wide"><span className="text-12-tamil-900">{group.title}</span></div>;
        $[6] = group.title;
        $[7] = t4;
      } else {
        t4 = $[7];
      }
      t1 = `p-5 grid grid-cols-2 ${GRID_COLS_CLASSES[group.columns] || GRID_COLS_CLASSES[3]} gap-y-4 gap-x-6 text-FFFFFF text-12-tamil-500`;
      t2 = resolvedLinks.map(_temp2);
    }
    $[0] = group;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[8] !== t1 || $[9] !== t2) {
    t6 = <div className={t1}>{t2}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t6) {
    t7 = <div className={t3}>{t4}{t6}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}

// Static Quick Links row — sourced from the Builder-driven header-footer
// config's footer.quickLinks (About Us, Privacy Policy, Terms, etc.), NOT
// the Quintype menu-groups API. openInNewTab is honored explicitly (in
// addition to cross-host detection) since a quick link can point at an
// internal path but still be flagged to open in a new tab.
function _temp2(link_0) {
  return <FooterLink key={link_0.id} href={link_0.href} external={link_0.external}>{link_0.label}</FooterLink>;
}
function _temp(link) {
  return <FooterLink key={link.id} href={link.href} external={link.external}>{link.label}</FooterLink>;
}
function FooterQuickLinks(t0) {
  const $ = _c(7);
  const {
    links
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== links) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const resolvedLinks = links.map(_temp3).filter(_temp4);
      if (resolvedLinks.length === 0) {
        t3 = null;
        break bb0;
      }
      t1 = "flex flex-wrap items-center gap-x-8 gap-y-3 text-FFFFFF text-12-english-500 border-b border-2D497E p-5";
      t2 = resolvedLinks.map(_temp5);
    }
    $[0] = links;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t2) {
    t4 = <div className={t1}>{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}

// Static "செய்திப் பிரிவுகள்" (sections) block — always rendered, sourced from
// the Quintype menu-groups API's "custom-footer-section-menu" group rather
// than the Builder-driven header-footer config, since that group isn't
// something Builder's header-footer editor manages.
function _temp5(link_0) {
  return <FooterLink key={link_0.id} href={link_0.href} external={link_0.external}>{link_0.label}</FooterLink>;
}
function _temp4(l) {
  return l !== null;
}
function _temp3(link) {
  const resolved = resolveFooterHref(link.url);
  if (!resolved) {
    return null;
  }
  return {
    id: link.id,
    label: link.label,
    href: resolved.href,
    external: resolved.external || link.openInNewTab === true
  };
}
const FOOTER_SECTIONS_TITLE = "செய்திப் பிரிவுகள்";
function FooterSectionsMenu(t0) {
  const $ = _c(14);
  const {
    items
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== items) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const resolvedLinks = items.map(_temp6).filter(_temp7);
      if (resolvedLinks.length === 0) {
        t5 = null;
        break bb0;
      }
      t3 = "border-b border-2D497E";
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <div className="flex p-5 items-center gap-2 text-FFFFFF text-12-english-500 uppercase tracking-wide"><span className="text-12-tamil-900">{FOOTER_SECTIONS_TITLE}</span></div>;
        $[6] = t4;
      } else {
        t4 = $[6];
      }
      t1 = `p-5 grid grid-cols-2 ${GRID_COLS_CLASSES[3]} gap-y-4 gap-x-6 text-FFFFFF text-12-tamil-500`;
      t2 = resolvedLinks.map(_temp8);
    }
    $[0] = items;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[7] !== t1 || $[8] !== t2) {
    t6 = <div className={t1}>{t2}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t6) {
    t7 = <div className={t3}>{t4}{t6}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}

// Right-column group: rendered like the original "THE NEW INDIAN EXPRESS
// GROUP" block — yellow uppercase heading + a plain vertical link list.
function _temp8(link) {
  return <FooterLink key={link.id} href={link.href} external={link.external}>{link.label}</FooterLink>;
}
function _temp7(l) {
  return l !== null;
}
function _temp6(item) {
  const resolved = resolveFooterHref(item.url || item.data?.link);
  return resolved ? {
    id: item.id,
    label: item.title as string,
    ...resolved
  } : null;
}
function FooterRightColumnGroup(t0) {
  const $ = _c(13);
  const {
    group
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== group) {
    const resolvedLinks = resolveGroupLinks(group);
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        color: "#FFDB59"
      };
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    if ($[5] !== group.title) {
      t3 = <div className="text-12-english-500 uppercase tracking-wide" style={t4}>{group.title}</div>;
      $[5] = group.title;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t1 = "mt-5 space-y-6 text-FFFFFF text-12-english-500";
    t2 = resolvedLinks.map(_temp9);
    $[0] = group;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2) {
    t4 = <ul className={t1}>{t2}</ul>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
function _temp9(link) {
  return <li key={link.id}><FooterLink href={link.href} external={link.external}>{link.label}</FooterLink></li>;
}
export default function Footer(t0) {
  const $ = _c(76);
  const {
    headerFooterConfig: headerFooterConfigProp
  } = t0;
  const [headerFooterConfig, setHeaderFooterConfig] = useState(headerFooterConfigProp ?? null);
  const cfg = headerFooterConfig ?? DEFAULT_HEADER_FOOTER_CONFIG;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [latestNews, setLatestNews] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [footerSectionItems, setFooterSectionItems] = useState(t2);
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      let cancelled = false;
      (async () => {
        const data = await getMenuGroups();
        const items = data?.["menu-groups"]?.["custom-footer-section-menu"]?.items ?? [];
        const cleaned = items.filter(_temp10);
        if (!cancelled) {
          setFooterSectionItems(cleaned);
        }
      })();
      return () => {
        cancelled = true;
      };
    };
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[4] !== headerFooterConfigProp) {
    t5 = () => {
      if (headerFooterConfigProp) {
        return;
      }
      let cancelled_0 = false;
      (async () => {
        const data_0 = await getHeaderFooterConfig();
        if (!cancelled_0 && data_0) {
          setHeaderFooterConfig(data_0);
        }
      })();
      return () => {
        cancelled_0 = true;
      };
    };
    t6 = [headerFooterConfigProp];
    $[4] = headerFooterConfigProp;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[7] !== cfg.footer.recentNews.count || $[8] !== cfg.footer.recentNews.enabled) {
    t7 = () => {
      if (!cfg.footer.recentNews.enabled) {
        return;
      }
      let cancelled_1 = false;
      getLatestNewsHeadlines(cfg.footer.recentNews.count).then(items_0 => {
        if (!cancelled_1) {
          setLatestNews(items_0);
        }
      });
      return () => {
        cancelled_1 = true;
      };
    };
    t8 = [cfg.footer.recentNews.enabled, cfg.footer.recentNews.count];
    $[7] = cfg.footer.recentNews.count;
    $[8] = cfg.footer.recentNews.enabled;
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  useEffect(t7, t8);
  const groups = cfg.footer.groups;
  const rightGroup = groups.length > 0 ? groups[groups.length - 1] : null;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t9;
  if ($[11] !== cfg.footer.appLinks || $[12] !== cfg.footer.logo.alt || $[13] !== cfg.footer.logo.url || $[14] !== cfg.footer.socialLinks || $[15] !== cfg.footer.tagline || $[16] !== groups) {
    const centerGroups = groups.length > 0 ? groups.slice(0, -1) : [];
    t15 = "w-full bg-012061 text-FFFFFF relative";
    t14 = "border-x border-2D497E";
    t13 = "max-w-[1245px] w-full mx-auto";
    t11 = "relative grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)_300px] lg:gap-0 border-b lg:border-y border-2D497E";
    let t16;
    if ($[24] !== cfg.footer.logo.url) {
      t16 = resolveAssetUrl(cfg.footer.logo.url);
      $[24] = cfg.footer.logo.url;
      $[25] = t16;
    } else {
      t16 = $[25];
    }
    let t17;
    if ($[26] !== cfg.footer.logo.alt || $[27] !== t16) {
      t17 = <div className="flex items-center justify-center p-5"><Image src={t16} alt={cfg.footer.logo.alt} width={180} height={78} className="w-[180px] h-auto" loading="lazy" /></div>;
      $[26] = cfg.footer.logo.alt;
      $[27] = t16;
      $[28] = t17;
    } else {
      t17 = $[28];
    }
    let t18;
    if ($[29] !== cfg.footer.tagline) {
      t18 = <div className="flex items-center gap-2 text-FFFFFF p-5 border-t border-2D497E"><span className="text-12-tamil-900">{cfg.footer.tagline}</span></div>;
      $[29] = cfg.footer.tagline;
      $[30] = t18;
    } else {
      t18 = $[30];
    }
    let t19;
    if ($[31] !== cfg.footer.socialLinks) {
      t19 = <div className="p-5"><SocialIcons useGrid={false} links={cfg.footer.socialLinks} /></div>;
      $[31] = cfg.footer.socialLinks;
      $[32] = t19;
    } else {
      t19 = $[32];
    }
    let t20;
    if ($[33] !== cfg.footer.appLinks) {
      t20 = cfg.footer.appLinks.length > 0 && <><div className="flex items-center gap-2 text-FFFFFF p-5 border-t border-2D497E"><span className="text-12-tamil-900">செயலிகளை பதிவிறக்க</span></div><div className="p-5 flex items-center gap-3">{cfg.footer.appLinks.map(_temp11)}</div></>;
      $[33] = cfg.footer.appLinks;
      $[34] = t20;
    } else {
      t20 = $[34];
    }
    if ($[35] !== t17 || $[36] !== t18 || $[37] !== t19 || $[38] !== t20) {
      t12 = <div className="pr-0 lg:border-l border-2D497E">{t17}{t18}{t19}{t20}</div>;
      $[35] = t17;
      $[36] = t18;
      $[37] = t19;
      $[38] = t20;
      $[39] = t12;
    } else {
      t12 = $[39];
    }
    t9 = "lg:border-x border-2D497E";
    t10 = centerGroups.map(_temp12);
    $[11] = cfg.footer.appLinks;
    $[12] = cfg.footer.logo.alt;
    $[13] = cfg.footer.logo.url;
    $[14] = cfg.footer.socialLinks;
    $[15] = cfg.footer.tagline;
    $[16] = groups;
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
    $[22] = t15;
    $[23] = t9;
  } else {
    t10 = $[17];
    t11 = $[18];
    t12 = $[19];
    t13 = $[20];
    t14 = $[21];
    t15 = $[22];
    t9 = $[23];
  }
  let t16;
  if ($[40] !== cfg.footer.quickLinks) {
    t16 = <FooterQuickLinks links={cfg.footer.quickLinks} />;
    $[40] = cfg.footer.quickLinks;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== footerSectionItems) {
    t17 = <FooterSectionsMenu items={footerSectionItems} />;
    $[42] = footerSectionItems;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== cfg.footer.copyrightText) {
    t18 = <div className="p-5 border-t border-2D497E text-FFFFFF text-12-tamil-500">{cfg.footer.copyrightText}</div>;
    $[44] = cfg.footer.copyrightText;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t10 || $[47] !== t16 || $[48] !== t17 || $[49] !== t18 || $[50] !== t9) {
    t19 = <div className={t9}>{t10}{t16}{t17}{t18}</div>;
    $[46] = t10;
    $[47] = t16;
    $[48] = t17;
    $[49] = t18;
    $[50] = t9;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== rightGroup) {
    t20 = rightGroup && <FooterRightColumnGroup group={rightGroup} />;
    $[52] = rightGroup;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <button aria-label="Scroll to top" className="absolute bottom-4 right-4 inline-flex items-center justify-center w-[40px] h-[40px] rounded-full bg-FFDB59 shadow-md hover:shadow-lg transition-shadow cursor-pointer" onClick={_temp13}><FaAngleUp size={22} color="#000000" aria-hidden={true} /></button>;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t20) {
    t22 = <div className="pl-5 lg:pl-8 pt-8 p-5 lg:border-r border-2D497E">{t20}{t21}</div>;
    $[55] = t20;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== t11 || $[58] !== t12 || $[59] !== t19 || $[60] !== t22) {
    t23 = <div className={t11}>{t12}{t19}{t22}</div>;
    $[57] = t11;
    $[58] = t12;
    $[59] = t19;
    $[60] = t22;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== cfg.footer.recentNews.enabled || $[63] !== cfg.footer.recentNews.title || $[64] !== latestNews) {
    t24 = cfg.footer.recentNews.enabled && latestNews.length > 0 && <div className="border-b border-2D497E"><div className="flex p-5 items-center gap-2 text-FFFFFF text-12-english-500 uppercase tracking-wide"><span className="text-12-tamil-900">{cfg.footer.recentNews.title}</span></div><div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-5">{latestNews.map(_temp14)}</div></div>;
    $[62] = cfg.footer.recentNews.enabled;
    $[63] = cfg.footer.recentNews.title;
    $[64] = latestNews;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  let t25;
  if ($[66] !== t13 || $[67] !== t23 || $[68] !== t24) {
    t25 = <div className={t13}>{t23}{t24}</div>;
    $[66] = t13;
    $[67] = t23;
    $[68] = t24;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  let t26;
  if ($[70] !== t14 || $[71] !== t25) {
    t26 = <div className={t14}>{t25}</div>;
    $[70] = t14;
    $[71] = t25;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  let t27;
  if ($[73] !== t15 || $[74] !== t26) {
    t27 = <footer className={t15}>{t26}</footer>;
    $[73] = t15;
    $[74] = t26;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  return t27;
}
function _temp14(item) {
  return <div key={item.id} className="pb-4 border-b border-2D497E"><FooterLink href={`/${item.slug}`}>{item.headline}</FooterLink></div>;
}
function _temp13() {
  return window.scrollTo({
    top: 0,
    behavior: "smooth"
  });
}
function _temp12(group) {
  return <FooterGroupSection key={group.id} group={group} />;
}
function _temp11(app) {
  const badgeSrc = app.imageUrl ? resolveAssetUrl(app.imageUrl) : APP_BADGE_IMAGES[app.label]?.src;
  return <Link key={app.id} href={app.url} target={app.openInNewTab === false ? undefined : "_blank"} rel={app.openInNewTab === false ? undefined : "noreferrer"} className="hover:opacity-80 transition-opacity">{badgeSrc ? <Image src={badgeSrc} alt={app.label} width={128} height={38} className="w-[128px] h-[38px] object-contain" loading="lazy" /> : <span className="text-FFFFFF text-12-tamil-600 underline">{app.label}</span>}</Link>;
}
function _temp10(it) {
  return Boolean(it?.title) && Boolean(it?.url || it?.data?.link);
}